/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { getIcon } from '@slr/shared-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';
import { Dropdown, RowProps } from 'react-bootstrap';
import { Control, FieldValues, Path, useController } from 'react-hook-form';

import './SLRDropdownForm.scss';

export type SLRDropdownFormProps<T extends FieldValues> = {
  id: string;
  control: Control<T>;
  name: Path<T>;
  options: { value: string; label: string }[];
  isInvalid?: boolean;
  placeholder: string;
  className?: RowProps['className'];
};

export const SLRDropdownForm = <T extends FieldValues>({
  id,
  control,
  name,
  options,
  isInvalid,
  placeholder,
  className = ''
}: SLRDropdownFormProps<T>) => {
  const {
    field: { value, onChange }
  } = useController({
    name,
    control
  });

  const selectedLabel = useMemo(() => {
    if (value === null || value === undefined) return placeholder;
    const foundOption = options.find(
      (o) =>
        o.value.toLocaleLowerCase() === (value as string).toLocaleLowerCase()
    );
    return foundOption?.label ?? placeholder;
  }, [value, placeholder, options]);

  return (
    <Dropdown className={`slr-dropdown-form ${className}`}>
      <Dropdown.Toggle
        id={id}
        variant="outline-neutral"
        size="lg"
        className={`form-toggle ${isInvalid ? 'border-danger' : ''}`}
      >
        {selectedLabel}
        <FontAwesomeIcon icon={getIcon('fas', 'sort')} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {options.map((option) => (
          <Dropdown.Item
            key={option.value}
            active={option === value}
            className="form-item"
            onClick={() => {
              onChange(option.value);
            }}
          >
            {option.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};
